/**
 * @fileoverview Number and currency formatting utilities
 * @module utils/formatting
 */

/**
 * Calculate sum of array values
 * @param {Array} array - Array of numbers or objects
 * @param {string} key - Object key to sum (optional)
 * @returns {number} Sum of values
 *
 * @example
 * total([1, 2, 3]) // 6
 * total([{amount: 100}, {amount: 200}], 'amount') // 300
 */
export const total = (array, key) => {
  if (!Array.isArray(array)) return 0;
  return key
    ? array.reduce((sum, item) => Number(sum) + (Number(item[key]) || 0), 0)
    : array.reduce((sum, item) => Number(sum) + (Number(item) || 0), 0);
};

/**
 * Validate decimal number format
 * @param {string|number} num - Number to validate
 * @param {number} length - Max decimal places (default: 2)
 * @returns {boolean} True if valid decimal format
 *
 * @example
 * float('123.45') // true
 * float('123.456', 2) // false
 * float('123.456', 3) // true
 */
export const float = (num, length = 2) => {
  const regexPattern = new RegExp(`^\\d*\\.?\\d{0,${length}}$`);
  return regexPattern.test(num);
};

/**
 * Format number with thousand separators and decimal places
 * @param {number|string} number - Number to format
 * @param {number} size - Decimal places (default: 2)
 * @param {boolean} ceil - Round up if true
 * @returns {string} Formatted number string
 *
 * @example
 * toFixed(1234567.89) // "1,234,567.89"
 * toFixed(1234567.894, 2) // "1,234,567.89"
 * toFixed(1234567.894, 2, true) // "1,234,567.90"
 */
export const toFixed = (number, size = 2, ceil = false) => {
  if (!number && number !== 0) return '0.00';
  if (number === 0) return '0.00';

  const num = Number(number.toString());
  const precision = size || 2;
  const processedNum = ceil
    ? Math.ceil(num * Math.pow(10, precision)) / Math.pow(10, precision)
    : num;

  return processedNum.toFixed(precision).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

/**
 * Format number as currency (Thai Baht)
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency symbol (default: '')
 * @returns {string} Formatted currency string
 *
 * @example
 * formatCurrency(1234567.89) // "1,234,567.89"
 */
export const formatCurrency = (amount, currency = '') => {
  return `${currency}${toFixed(amount)}`;
};

/**
 * Format number with thousand separators (no decimals)
 * @param {number|string} number - Number to format
 * @returns {string} Formatted number
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export const formatNumber = (number) => {
  if (!number && number !== 0) return '0';
  return Number(number).toLocaleString('en-US');
};

/**
 * Format integer for display with thousand separators (read-only display only)
 * Non-numeric values return '-'. Accepts numeric strings such as "7676".
 * @example fmtInt(7676) // "7,676"
 */
export const fmtInt = (n) => {
  if (n === null || n === undefined || n === '') return '-';
  const num = Number(typeof n === 'string' ? n.replace(/,/g, '') : n);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('en-US');
};

/**
 * Format quantity with thousand separators and up to maxDec decimals (no forced trailing zeros)
 * Non-numeric values return '-'.
 * @example fmtQty(1234.5) // "1,234.5"
 */
export const fmtQty = (n, maxDec = 2) => {
  if (n === null || n === undefined || n === '') return '-';
  const num = Number(typeof n === 'string' ? n.replace(/,/g, '') : n);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('en-US', { maximumFractionDigits: maxDec });
};

/**
 * Parse formatted number string back to number
 * @param {string} formattedNumber - Formatted number string
 * @returns {number} Parsed number
 *
 * @example
 * parseFormattedNumber('1,234,567.89') // 1234567.89
 */
export const parseFormattedNumber = (formattedNumber) => {
  if (!formattedNumber) return 0;
  return Number(formattedNumber.toString().replace(/,/g, ''));
};

/**
 * Format decimal hours as "H:MM" (e.g. 4.5 -> "4:30")
 * ชั่วโมงแบบทศนิยมอ่านสับสนกับนาที (4.50 ไม่ใช่ 4 ชม. 50 นาที) จึงแสดงเป็น ชม.:นาที
 * @param {number|string} hours - Duration in decimal hours
 * @returns {string} Formatted "H:MM" ("-" when no value)
 *
 * @example
 * formatHoursMinutes(4.5) // "4:30"
 * formatHoursMinutes(96.34) // "96:20"
 */
export const formatHoursMinutes = (hours) => {
  if (hours === null || hours === undefined || Number.isNaN(Number(hours))) return '-';
  const totalMinutes = Math.round(Number(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const formattingUtils = {
  total,
  float,
  toFixed,
  formatCurrency,
  formatNumber,
  parseFormattedNumber,
  formatHoursMinutes,
};

export default formattingUtils;
